import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import i18next from 'i18next';
import { Activity, useActivityStore } from '@/stores/activityStore';
import { getAuthenticatedClient, getCurrentUserId } from '@/services/client';
import { toast } from '@/hooks/use-toast';
import { useCBSBatchStore } from '@/stores/cbsBatchStore';

// Fetch all activities
export const useActivities = () => {
  const { activities, addActivity: addToStore } = useActivityStore();

  return useQuery({
    queryKey: ['activities'],
    queryFn: async () => {
      try {
        const client = getAuthenticatedClient();
        const userId = await getCurrentUserId();
        if (!userId) return activities;

        const { data, error } = await client
          .from('activities')
          .select('*')
          .eq('user_id', userId)
          .is('deleted_at', null)
          .order('activity_date', { ascending: false });

        if (error) throw error;

        // Map DB rows to Activity interface
        return (data ?? []).map((row): Activity => ({
          id: row.id,
          type: (row.activity_type as Activity['type']) || 'other',
          title: row.activity_type,
          description: row.notes ?? '',
          date: row.activity_date,
          photos: [],
          points: row.points_earned,
          tags: [],
          createdAt: row.created_at,
          updatedAt: row.updated_at,
        }));
      } catch {
        return activities;
      }
    },
    staleTime: 1000 * 60 * 5,
    initialData: activities,
  });
};

// Fetch single activity
export const useActivity = (id: string) => {
  return useQuery({
    queryKey: ['activities', id],
    queryFn: async () => {
      const client = getAuthenticatedClient();
      const { data, error } = await client
        .from('activities')
        .select('*')
        .eq('id', id)
        .single();
      if (error) throw error;
      return data as unknown as Activity;
    },
    enabled: !!id,
  });
};

// Create activity
export const useCreateActivity = () => {
  const queryClient = useQueryClient();
  const { addActivity } = useActivityStore();
  const activeBatchId = useCBSBatchStore((s) => s.activeBatch?.id) ?? null;

  return useMutation({
    mutationFn: async (input: Omit<Activity, 'id' | 'createdAt' | 'updatedAt'>) => {
      const client = getAuthenticatedClient();
      const userId = await getCurrentUserId();
      if (!userId) throw new Error('Not authenticated');

      const { data, error } = await client
        .from('activities')
        .insert({
          user_id: userId,
          activity_type: input.type as any,
          activity_date: input.date,
          notes: input.description,
          points_earned: input.points,
          batch_id: activeBatchId,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },

    onMutate: async (newActivity) => {
      await queryClient.cancelQueries({ queryKey: ['activities'] });
      const previousActivities = queryClient.getQueryData<Activity[]>(['activities']);

      const optimisticActivity: Activity = {
        ...newActivity,
        id: `temp-${Date.now()}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      queryClient.setQueryData<Activity[]>(['activities'], (old = []) => [
        optimisticActivity,
        ...old,
      ]);
      addActivity(optimisticActivity);
      return { previousActivities };
    },

    onError: (error, _, context) => {
      if (context?.previousActivities) {
        queryClient.setQueryData(['activities'], context.previousActivities);
      }
      toast({
        title: i18next.t("activity.createFailed", { defaultValue: "Failed to create activity" }),
        description: error instanceof Error ? error.message : i18next.t("common.unknownError", { defaultValue: "Unknown error" }),
        variant: 'destructive',
      });
    },

    onSuccess: () => {
      toast({ title: i18next.t("activity.created", { defaultValue: "Activity created" }), description: i18next.t("activity.createdDesc", { defaultValue: "Your activity has been logged successfully" }) });
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
  });
};

// Update activity
export const useUpdateActivity = () => {
  const queryClient = useQueryClient();
  const { updateActivity } = useActivityStore();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Activity> }) => {
      const client = getAuthenticatedClient();
      const { error } = await client
        .from('activities')
        .update({ notes: data.description, activity_type: data.type as any })
        .eq('id', id);
      if (error) throw error;
    },

    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: ['activities'] });
      const previousActivities = queryClient.getQueryData<Activity[]>(['activities']);
      queryClient.setQueryData<Activity[]>(['activities'], (old = []) =>
        old.map((a) => (a.id === id ? { ...a, ...data } : a))
      );
      updateActivity(id, data);
      return { previousActivities };
    },

    onError: (error, _, context) => {
      if (context?.previousActivities) {
        queryClient.setQueryData(['activities'], context.previousActivities);
      }
      toast({ title: i18next.t("activity.updateFailed", { defaultValue: "Failed to update activity" }), description: error instanceof Error ? error.message : i18next.t("common.unknownError", { defaultValue: "Unknown error" }), variant: 'destructive' });
    },

    onSuccess: () => {
      toast({ title: i18next.t("activity.updated", { defaultValue: "Activity updated" }), description: i18next.t("common.changesSaved", { defaultValue: "Your changes have been saved" }) });
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
  });
};

// Delete activity
export const useDeleteActivity = () => {
  const queryClient = useQueryClient();
  const { deleteActivity } = useActivityStore();

  return useMutation({
    mutationFn: async (id: string) => {
      const client = getAuthenticatedClient();
      const { error } = await client
        .from('activities')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },

    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['activities'] });
      const previousActivities = queryClient.getQueryData<Activity[]>(['activities']);
      queryClient.setQueryData<Activity[]>(['activities'], (old = []) =>
        old.filter((a) => a.id !== id)
      );
      deleteActivity(id);
      return { previousActivities };
    },

    onError: (error, _, context) => {
      if (context?.previousActivities) {
        queryClient.setQueryData(['activities'], context.previousActivities);
      }
      toast({ title: i18next.t("activity.deleteFailed", { defaultValue: "Failed to delete activity" }), description: error instanceof Error ? error.message : i18next.t("common.unknownError", { defaultValue: "Unknown error" }), variant: 'destructive' });
    },

    onSuccess: () => {
      toast({ title: i18next.t("activity.deleted", { defaultValue: "Activity deleted" }), description: i18next.t("activity.deletedDesc", { defaultValue: "The activity has been removed" }) });
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
  });
};
